import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Loader2, Users, TrendingUp, Gem, ChevronDown, ChevronRight, CheckCircle, User, Briefcase, ShoppingCart, Eye } from 'lucide-react';
import { multiNivelService, GetNetworkResponse, DirectChildDto } from '@/services/multi-nivel.service';
import { formatCurrency } from '@/services/homeDetailsService';

interface NetworkVisualizationProps {
  usuarioId: number;
}

interface FilterState {
  searchTerm: string;
  filterNivel: number | null;
  onlyMeta: boolean;
}

interface MetaStats {
  nivel1: { total: number; comMeta: number; percentual: number };
  nivel2: { total: number; comMeta: number; percentual: number };
  nivel3: { total: number; comMeta: number; percentual: number };
  geral: number;
}

export const NetworkVisualization: React.FC<NetworkVisualizationProps> = ({ usuarioId }) => {
  const [networkData, setNetworkData] = useState<GetNetworkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'graphical'>('tree');
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    filterNivel: null,
    onlyMeta: false,
  });
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  // Cache de filhos por ID do nó — lazy loading
  const [childrenCache, setChildrenCache] = useState<Map<number, DirectChildDto[]>>(new Map());
  // IDs de nós que estão carregando filhos
  const [loadingNodes, setLoadingNodes] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadNetworkData();
  }, [usuarioId]);

  const fetchAndCacheChildren = useCallback(async (nodeId: number): Promise<DirectChildDto[]> => {
    // Cache hit
    if (childrenCache.has(nodeId)) {
      return childrenCache.get(nodeId)!;
    }

    setLoadingNodes(prev => new Set(prev).add(nodeId));
    try {
      const children = await multiNivelService.getDirectChildren(nodeId);
      setChildrenCache(prev => {
        const next = new Map(prev);
        next.set(nodeId, children);
        return next;
      });
      return children;
    } finally {
      setLoadingNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }
  }, [childrenCache]);

  const loadNetworkData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await multiNivelService.getNetworkStructure(usuarioId);
      setNetworkData(data);

      // Carrega imediatamente os filhos diretos do root
      const rootChildren = await multiNivelService.getDirectChildren(data.usuarioPrincipal.id);
      setChildrenCache(new Map([[data.usuarioPrincipal.id, rootChildren]]));
      setExpandedNodes(new Set([data.usuarioPrincipal.id]));
    } catch (err) {
      console.error('Erro ao carregar rede:', err);
      setError('Erro ao carregar dados da rede');
    } finally {
      setLoading(false);
    }
  };

  const toggleNodeExpansion = useCallback(async (nodeId: number) => {
    // Se está expandido, apenas colapsa — sem nova requisição
    if (expandedNodes.has(nodeId)) {
      setExpandedNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return;
    }

    // Expande: busca filhos se ainda não estão no cache
    await fetchAndCacheChildren(nodeId);
    setExpandedNodes(prev => new Set(prev).add(nodeId));
  }, [expandedNodes, fetchAndCacheChildren]);

  const calculateMetaStats = (): MetaStats => {
    if (!networkData) {
      return {
        nivel1: { total: 0, comMeta: 0, percentual: 0 },
        nivel2: { total: 0, comMeta: 0, percentual: 0 },
        nivel3: { total: 0, comMeta: 0, percentual: 0 },
        geral: 0,
      };
    }

    const stats = {
      nivel1: { total: 0, comMeta: 0 },
      nivel2: { total: 0, comMeta: 0 },
      nivel3: { total: 0, comMeta: 0 },
    };

    networkData.indicacoes.forEach((ind) => {
      const nivel = `nivel${ind.nivel}` as keyof typeof stats;
      if (stats[nivel]) {
        stats[nivel].total++;
        if (ind.metMinimoBatido) {
          stats[nivel].comMeta++;
        }
      }
    });

    const totalComMeta = stats.nivel1.comMeta + stats.nivel2.comMeta + stats.nivel3.comMeta;
    const totalIndicacoes = networkData.estatisticas.totalIndicacoes;
    const geralPercentual =
      totalIndicacoes > 0 ? Math.round((totalComMeta / totalIndicacoes) * 100) : 0;

    return {
      nivel1: {
        total: stats.nivel1.total,
        comMeta: stats.nivel1.comMeta,
        percentual:
          stats.nivel1.total > 0 ? Math.round((stats.nivel1.comMeta / stats.nivel1.total) * 100) : 0,
      },
      nivel2: {
        total: stats.nivel2.total,
        comMeta: stats.nivel2.comMeta,
        percentual:
          stats.nivel2.total > 0 ? Math.round((stats.nivel2.comMeta / stats.nivel2.total) * 100) : 0,
      },
      nivel3: {
        total: stats.nivel3.total,
        comMeta: stats.nivel3.comMeta,
        percentual:
          stats.nivel3.total > 0 ? Math.round((stats.nivel3.comMeta / stats.nivel3.total) * 100) : 0,
      },
      geral: geralPercentual,
    };
  };

  const metaStats = calculateMetaStats();

  // true quando o backend retornou dados financeiros completos (apenas para admin)
  const isAdminView = networkData?.estatisticas?.totalVendasRede !== null && networkData?.estatisticas?.totalVendasRede !== undefined;

  const getHealthColor = (percentual: number) => {
    if (percentual >= 75) return 'from-green-50 to-green-100';
    if (percentual >= 50) return 'from-blue-50 to-blue-100';
    if (percentual >= 25) return 'from-yellow-50 to-yellow-100';
    return 'from-red-50 to-red-100';
  };

  const getHealthTextColor = (percentual: number) => {
    if (percentual >= 75) return 'text-green-800';
    if (percentual >= 50) return 'text-blue-800';
    if (percentual >= 25) return 'text-yellow-800';
    return 'text-red-800';
  };
  
  

  const filteredIndicacoes = networkData?.indicacoes.filter((ind) => {
    const matchSearch = ind.nome_razao_social
      .toLowerCase()
      .includes(filters.searchTerm.toLowerCase());
    const matchNivel = filters.filterNivel === null || ind.nivel === filters.filterNivel;
    const matchMeta = !filters.onlyMeta || ind.metMinimoBatido;
    return matchSearch && matchNivel && matchMeta;
  }) || [];

  if (loading) {
    return (
      <div className="w-full h-48 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !networkData) {
    return (
      <div className="w-full p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error || 'Erro ao carregar dados da rede'}</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Admin-only: Total Vendas e Diamantes */}
      {isAdminView && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Total Vendas na Rede"
            value={`${formatCurrency(networkData.estatisticas.totalVendasRede!)}`}
            icon={TrendingUp}
            color="from-blue-50 to-blue-100"
            textColor="text-blue-800"
          />
          <StatCard
            title="Total Diamantes na Rede"
            value={networkData.estatisticas.totalDiamantesRede!.toFixed(2)}
            icon={Gem}
            color="from-purple-50 to-purple-100"
            textColor="text-purple-800"
          />
        </div>
      )}

      {/* Cards Principais + Distribuição por Nível */}
      <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-[1fr_1fr_0.7fr_0.7fr_0.7fr] md:gap-3 md:items-stretch">
        {/* Linha 1 no mobile: Total + Ativos */}
        <div className="grid grid-cols-2 gap-3 md:contents">
          {/* Total de Indicações */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200 flex items-center gap-3">
            <div className="p-2.5 bg-green-200 rounded-xl flex-shrink-0">
              <Users className="w-6 h-6 text-green-800" />
            </div>
            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Total de Indicações</p>
              <p className="text-4xl font-bold text-green-900">{networkData.estatisticas.totalIndicacoes}</p>
            </div>
          </div>

          {/* Ativos na Rede */}
          <div className={`bg-gradient-to-br ${getHealthColor(metaStats.geral)} rounded-xl p-4 border border-gray-200 flex items-center gap-3`}>
            <div className="p-2.5 bg-white/60 rounded-xl flex-shrink-0">
              <CheckCircle className={`w-6 h-6 ${getHealthTextColor(metaStats.geral)}`} />
            </div>
            <div>
              <p className={`text-xs font-semibold ${getHealthTextColor(metaStats.geral)} uppercase tracking-wider`}>Ativos na Rede</p>
              <p className={`text-4xl font-bold ${getHealthTextColor(metaStats.geral)}`}>
                {metaStats.geral}%
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {metaStats.nivel1.comMeta + metaStats.nivel2.comMeta + metaStats.nivel3.comMeta} de {networkData.estatisticas.totalIndicacoes}
              </p>
            </div>
          </div>
        </div>

        {/* Linha 2 no mobile: Níveis 1, 2, 3 */}
        <div className="grid grid-cols-3 gap-3 md:contents">
          {/* Nível 1 */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 text-center flex flex-col justify-center">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Nível 1</p>
            <p className="text-3xl font-bold text-blue-900">{networkData.estatisticas.porNivel.nivel1}</p>
          </div>

          {/* Nível 2 */}
          <div className="bg-green-50 rounded-xl p-4 border border-green-100 text-center flex flex-col justify-center">
            <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">Nível 2</p>
            <p className="text-3xl font-bold text-green-900">{networkData.estatisticas.porNivel.nivel2}</p>
          </div>

          {/* Nível 3 */}
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 text-center flex flex-col justify-center">
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">Nível 3</p>
            <p className="text-3xl font-bold text-purple-900">{networkData.estatisticas.porNivel.nivel3}</p>
          </div>
        </div>
      </div>

      {/* Metas por Nível — compacto */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Metas por Nível</p>
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          {[
            { label: 'Nível 1', stats: metaStats.nivel1 },
            { label: 'Nível 2', stats: metaStats.nivel2 },
            { label: 'Nível 3', stats: metaStats.nivel3 },
          ].map(({ label, stats }) => (
            <div key={label} className="px-5 first:pl-0 last:pr-0">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${getHealthTextColor(stats.percentual)}`}>{stats.percentual}%</p>
              <p className="text-xs text-gray-400 mt-0.5">{stats.comMeta} de {stats.total}</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    stats.percentual >= 75 ? 'bg-green-400' : stats.percentual >= 50 ? 'bg-blue-400' : stats.percentual >= 25 ? 'bg-yellow-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${stats.percentual}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toggle de Visualização */}
      <div className="flex gap-2">
        <button
          onClick={() => setViewMode('tree')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === 'tree'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          Visualização em Árvore
        </button>
        <button
          onClick={() => setViewMode('graphical')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            viewMode === 'graphical'
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          Visualização Gráfica
        </button>
      </div>

      {/* Organograma / Visualização Gráfica */}
      {viewMode === 'tree' ? (
        <Card className="shadow-md">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Organograma da Rede
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <div className="min-w-full space-y-2">
                {networkData.indicador && (
                  <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-sm font-semibold text-gray-600">Indicador</p>
                    <OrgChartNode
                      node={{ ...networkData.indicador, nivel: 1, hasChildren: false }}
                      isExpanded={expandedNodes.has(networkData.indicador.id)}
                      onToggle={toggleNodeExpansion}
                      expandedNodes={expandedNodes}
                      childrenCache={childrenCache}
                      loadingNodes={loadingNodes}
                      isAdmin={isAdminView}
                    />
                  </div>
                )}

                <OrgChartNode
                  node={{
                    id: networkData.usuarioPrincipal.id,
                    nome_razao_social: networkData.usuarioPrincipal.nome_razao_social,
                    codigo: networkData.usuarioPrincipal.codigo,
                    email: networkData.usuarioPrincipal.email,
                    role: networkData.usuarioPrincipal.role as any,
                    nivel: 0,
                    totalVendas: 0,
                    totalDiamantes: 0,
                    metMinimoBatido: false,
                    hasChildren: (childrenCache.get(networkData.usuarioPrincipal.id)?.length ?? 0) > 0,
                  }}
                  isExpanded={expandedNodes.has(networkData.usuarioPrincipal.id)}
                  onToggle={toggleNodeExpansion}
                  expandedNodes={expandedNodes}
                  childrenCache={childrenCache}
                  loadingNodes={loadingNodes}
                  isRoot
                  isAdmin={isAdminView}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <GraphicalNetworkView
          data={networkData}
          childrenCache={childrenCache}
          loadingNodes={loadingNodes}
          onLoadChildren={fetchAndCacheChildren}
        />
      )}

      {/* Filtros e Tabela de Indicações */}
      <Card className="shadow-md">
        <CardHeader className="border-b">
          <CardTitle>Tabela de Indicações</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Buscar por nome..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            />
            <Select
              value={filters.filterNivel ? filters.filterNivel.toString() : ''}
              onValueChange={(value) =>
                setFilters({
                  ...filters,
                  filterNivel: value ? parseInt(value) : null,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por nível..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os níveis</SelectItem>
                <SelectItem value="1">Nível 1</SelectItem>
                <SelectItem value="2">Nível 2</SelectItem>
                <SelectItem value="3">Nível 3</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="onlyMeta"
                checked={filters.onlyMeta}
                onChange={(e) => setFilters({ ...filters, onlyMeta: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="onlyMeta" className="text-sm font-medium cursor-pointer">
                Apenas com meta
              </label>
            </div>

            <p className="text-sm text-gray-600 flex items-center">
              ({filteredIndicacoes.length} resultado{filteredIndicacoes.length !== 1 ? 's' : ''})
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  {isAdminView && <TableHead>Email</TableHead>}
                  <TableHead>Nível</TableHead>
                  {isAdminView && <TableHead>Vendas (R$)</TableHead>}
                  {isAdminView && <TableHead>Diamantes</TableHead>}
                  <TableHead>Meta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIndicacoes.length > 0 ? (
                  filteredIndicacoes.map((indicacao) => (
                    <TableRow key={indicacao.id}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span>{indicacao.nome_razao_social}</span>
                          <span className="text-xs text-gray-500 font-normal">#{indicacao.codigo || '-'}</span>
                        </div>
                      </TableCell>
                      {isAdminView && <TableCell className="text-sm">{indicacao.email}</TableCell>}
                      <TableCell>
                        <Badge variant="outline">{indicacao.nivel}</Badge>
                      </TableCell>
                      {isAdminView && <TableCell>R$ {indicacao.totalVendas!.toFixed(2)}</TableCell>}
                      {isAdminView && <TableCell>{indicacao.totalDiamantes}</TableCell>}
                      <TableCell>
                        <Badge
                          variant={indicacao.metMinimoBatido ? 'default' : 'secondary'}
                          className={
                            indicacao.metMinimoBatido
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }
                        >
                          {indicacao.metMinimoBatido ? '✓ Atingida' : '✗ Não atingida'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isAdminView ? 6 : 3} className="text-center text-gray-500 py-4">
                      Nenhuma indicação encontrada com os filtros aplicados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  textColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, textColor }) => (
  <div className={`bg-gradient-to-br ${color} rounded-lg p-4 border border-gray-200`}>
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-sm font-semibold ${textColor} uppercase mb-1`}>{title}</p>
        <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
      </div>
      <Icon className={`w-8 h-8 ${textColor} opacity-50`} />
    </div>
  </div>
);



interface GraphicalNetworkViewProps {
  data: GetNetworkResponse;
  childrenCache: Map<number, DirectChildDto[]>;
  loadingNodes: Set<number>;
  onLoadChildren: (nodeId: number) => Promise<DirectChildDto[]>;
}

const GraphicalNetworkView: React.FC<GraphicalNetworkViewProps> = ({ data, childrenCache, loadingNodes, onLoadChildren }) => {
  const [loadingLevel, setLoadingLevel] = useState<2 | 3 | null>(null);
  // Flags explícitas: só true quando o usuário clica em "Carregar nível X" nesta view
  const [nivel2LoadedHere, setNivel2LoadedHere] = useState(false);
  const [nivel3LoadedHere, setNivel3LoadedHere] = useState(false);

  const nivel1: DirectChildDto[] = childrenCache.get(data.usuarioPrincipal.id) ?? [];

  const nivel2: DirectChildDto[] = [];
  if (nivel2LoadedHere) {
    for (const n1 of nivel1) {
      const children = childrenCache.get(n1.id);
      if (children) nivel2.push(...children);
    }
  }

  const nivel3: DirectChildDto[] = [];
  if (nivel3LoadedHere) {
    for (const n2 of nivel2) {
      const children = childrenCache.get(n2.id);
      if (children) nivel3.push(...children);
    }
  }

  const handleLoadNivel2 = async () => {
    if (nivel1.length === 0) return;
    setLoadingLevel(2);
    try {
      await Promise.all(nivel1.map(n => onLoadChildren(n.id)));
      setNivel2LoadedHere(true);
    } finally {
      setLoadingLevel(null);
    }
  };

  const handleLoadNivel3 = async () => {
    if (nivel2.length === 0) return;
    setLoadingLevel(3);
    try {
      await Promise.all(nivel2.map(n => onLoadChildren(n.id)));
      setNivel3LoadedHere(true);
    } finally {
      setLoadingLevel(null);
    }
  };

  const Circle: React.FC<{ user: DirectChildDto; size?: string; iconSize?: string }> = ({ user, size = 'w-9 h-9 sm:w-12 sm:h-12', iconSize = 'w-4 h-4 sm:w-6 sm:h-6' }) => (
    <div className="flex flex-col items-center">
      <div
        className={`${size} rounded-full flex items-center justify-center font-bold text-white text-xs cursor-help ${
          user.metMinimoBatido ? 'bg-green-500' : 'bg-orange-400'
        } hover:shadow-lg transition-shadow`}
        title={`#${user.codigo} ${user.nome_razao_social}${user.totalVendas !== null ? `\nR$ ${user.totalVendas.toFixed(2)}` : ''}${user.totalDiamantes !== null ? `\n💎 ${user.totalDiamantes}` : ''}`}
      >
        {user.role === 'revendedora' ? (
          <Briefcase className={iconSize} />
        ) : (
          <ShoppingCart className={iconSize} />
        )}
      </div>
      <p className="text-xs mt-1 text-center max-w-[60px] text-gray-700 font-semibold">
        {user.nome_razao_social.split(' ')[0]}
      </p>
    </div>
  );

  return (
    <Card className="shadow-md">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Visualização Gráfica da Rede
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-8">
          {/* Nível 0 - Usuário principal */}
          <div className="flex justify-center">
            <div className="flex flex-col items-center">
              <div
                className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-blue-500 flex items-center justify-center font-bold text-white text-2xl hover:shadow-lg transition-shadow cursor-help"
                title={data.usuarioPrincipal.nome_razao_social}
              >
                <User className="w-7 h-7 sm:w-10 sm:h-10" />
              </div>
              <p className="text-sm mt-2 font-bold text-center text-blue-800">
                {data.usuarioPrincipal.nome_razao_social}
              </p>
            </div>
          </div>

          {/* Nível 1 */}
          {nivel1.length > 0 && (
            <div>
              <p className="text-center text-sm font-bold text-purple-600 mb-4">
                Nível 1 — {nivel1.length} indicaç{nivel1.length === 1 ? 'ão' : 'ões'}
              </p>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
                {nivel1.map((user) => (
                  <Circle key={user.id} user={user} size="w-9 h-9 sm:w-16 sm:h-16" iconSize="w-4 h-4 sm:w-6 sm:h-6" />
                ))}
              </div>
            </div>
          )}

          {/* Botão / spinner carregar nível 2 */}
          {nivel1.length > 0 && !nivel2LoadedHere && (
            <div className="flex justify-center">
              <button
                onClick={handleLoadNivel2}
                disabled={loadingLevel === 2}
                className="px-4 py-2 rounded-lg font-medium bg-pink-100 text-pink-700 hover:bg-pink-200 transition-all flex items-center gap-2 disabled:opacity-60"
              >
                {loadingLevel === 2 ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Carregando nível 2...</>
                ) : (
                  'Carregar nível 2'
                )}
              </button>
            </div>
          )}

          {/* Nível 2 */}
          {nivel2.length > 0 && (
            <div>
              <p className="text-center text-sm font-bold text-pink-600 mb-4">
                Nível 2 — {nivel2.length} indicaç{nivel2.length === 1 ? 'ão' : 'ões'}
              </p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                {nivel2.map((user) => (
                  <Circle key={user.id} user={user} size="w-8 h-8 sm:w-14 sm:h-14" iconSize="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                ))}
              </div>
            </div>
          )}

          {/* Botão / spinner carregar nível 3 */}
          {nivel2LoadedHere && nivel2.length > 0 && !nivel3LoadedHere && (
            <div className="flex justify-center">
              <button
                onClick={handleLoadNivel3}
                disabled={loadingLevel === 3}
                className="px-4 py-2 rounded-lg font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-all flex items-center gap-2 disabled:opacity-60"
              >
                {loadingLevel === 3 ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Carregando nível 3...</>
                ) : (
                  'Carregar nível 3'
                )}
              </button>
            </div>
          )}

          {/* Nível 3 */}
          {nivel3.length > 0 && (
            <div>
              <p className="text-center text-sm font-bold text-indigo-600 mb-4">
                Nível 3 — {nivel3.length} indicaç{nivel3.length === 1 ? 'ão' : 'ões'}
              </p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {nivel3.map((user) => (
                  <Circle key={user.id} user={user} size="w-7 h-7 sm:w-12 sm:h-12" iconSize="w-3 h-3 sm:w-5 sm:h-5" />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Legenda */}
        <div className="mt-8 pt-6 border-t flex gap-6 justify-center text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span>Com meta atingida</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-400"></div>
            <span>Meta não atingida</span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span>Revendedora</span>
          </div>
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span>Cliente</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface OrgChartNodeProps {
  node: {
    id: number;
    nome_razao_social: string;
    codigo: number | null;
    email?: string | null;
    role: string;
    nivel: number;
    totalVendas: number | null;
    totalDiamantes: number;
    metMinimoBatido: boolean;
    hasChildren: boolean;
    dateIndicacao?: string;
  };
  isExpanded: boolean;
  onToggle: (nodeId: number) => void;
  expandedNodes: Set<number>;
  childrenCache: Map<number, DirectChildDto[]>;
  loadingNodes: Set<number>;
  isRoot?: boolean;
  level?: number;
  isAdmin?: boolean;
}

const OrgChartNode: React.FC<OrgChartNodeProps> = ({
  node,
  isExpanded,
  onToggle,
  expandedNodes,
  childrenCache,
  loadingNodes,
  isRoot,
  level = 0,
  isAdmin = false,
}) => {
  const cachedChildren = childrenCache.get(node.id) ?? [];
  const isLoadingThis = loadingNodes.has(node.id);
  // hasChildren = flag do backend OU filhos já no cache
  const hasChildren = node.hasChildren || cachedChildren.length > 0;

  const getBackgroundColor = (metAcao: boolean) =>
    metAcao ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300';

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      const adjustedDate = new Date(date.getTime() - 3 * 60 * 60 * 1000);
      const day = String(adjustedDate.getDate()).padStart(2, '0');
      const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
      const year = String(adjustedDate.getFullYear()).slice(-2);
      const hours = String(adjustedDate.getHours()).padStart(2, '0');
      const minutes = String(adjustedDate.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return '-';
    }
  };

  return (
    <div className={`${level > 0 ? 'ml-6 mt-2' : ''}`}>
      <div
        className={`border-2 rounded-lg p-3 cursor-pointer transition-all hover:shadow-md ${getBackgroundColor(node.metMinimoBatido)} ${isRoot ? 'bg-blue-50 border-blue-300' : ''}`}
        onClick={() => (hasChildren || isLoadingThis) && onToggle(node.id)}
      >
        <div className="flex items-start gap-2">
          {isLoadingThis ? (
            <Loader2 className="w-4 h-4 mt-0.5 text-blue-500 animate-spin flex-shrink-0" />
          ) : hasChildren ? (
            <button className="mt-0.5 p-0 hover:bg-gray-200 rounded flex-shrink-0">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="w-4 flex-shrink-0" />
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">
              {node.nome_razao_social}{' '}
              <span className="text-muted-foreground">#{node.codigo || '-'}</span>
            </p>
            <div className="flex gap-4 items-center text-xs border-l border-gray-600 pl-2">
              {node.totalVendas !== null && (
                <span className="text-gray-700">
                  <strong>{formatCurrency(node.totalVendas)}</strong>
                </span>
              )}
              {node.totalDiamantes !== null && (
                <span className="text-gray-700">
                  <strong>{node.totalDiamantes}</strong>💎
                </span>
              )}
              {node.metMinimoBatido ? (
                <span className="text-green-700 font-semibold">✓ Ativo</span>
              ) : (
                <span className="text-orange-700 font-semibold">✗ Inativo</span>
              )}
            </div>
            <div className="flex gap-4 items-center text-xs border-l border-gray-600 pl-2">
              <span className="text-gray-700">
                <strong>{node.role.charAt(0).toLocaleUpperCase() + node.role.slice(1)}</strong>
              </span>
            </div>
            <div className="flex gap-4 items-center text-xs border-l border-gray-600 pl-2">
              <span className="text-gray-700">
                <strong>{formatDate(node.dateIndicacao)}</strong>
              </span>
            </div>
            {isAdmin && (
              <a
                href={`/v2/usuarios/${node.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-200 transition-colors"
                title="Ver perfil"
              >
                <Eye className="w-4 h-4 text-gray-600" />
              </a>
            )}
          </div>
        </div>
      </div>

      {isExpanded && cachedChildren.length > 0 && (
        <div className="mt-2 border-l-2 border-gray-300 pl-2">
          {cachedChildren.map((child) => (
            <OrgChartNode
              key={child.id}
              node={{ ...child, nivel: level + 1 }}
              isExpanded={expandedNodes.has(child.id)}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
              childrenCache={childrenCache}
              loadingNodes={loadingNodes}
              level={level + 1}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};
